// src/queries/partners/usePriceUploadDetailsCount.ts
//
// React Query hook: usePriceUploadDetailsCount
// - Fetches the total count of bulk upload detail rows for a partner.
// - Supports the "onlyFailed" filter.
// - The query key includes filter params to ensure automatic refetch
//   when the toggle state changes.
//
// Design notes
// - The backend count API returns the count at `data.data[0]`.
//   Normalization is handled inside the API layer.
// - This hook is intentionally lightweight and stateless,
//   delegating all normalization to the API function.

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getPriceUploadHistoryDetailsCount } from '../../apis/partnerBulk';
import { isValidPositiveNumber } from './priceUploadQueryUtils';

type Params = {
  onlyFailed?: boolean;
};

export const usePriceUploadDetailsCount = (
  partnerId: number,
  uploadId: number,
  params?: Params,
) => {
  const onlyFailed = Boolean(params?.onlyFailed);

  const queryOptions: UseQueryOptions<number, Error> = {
    /**
     * Query key
     * - Includes partnerId, uploadId, and filter state.
     * - Ensures correct cache separation and refetch on toggle changes.
     */
    queryKey: ['priceUploadDetailsCount', partnerId, uploadId, { onlyFailed }],

    /**
     * Enable condition
     * - Prevents accidental requests with invalid identifiers.
     * - Keeps hook usage safe even when params are not yet resolved.
     */
    enabled: isValidPositiveNumber(partnerId) && isValidPositiveNumber(uploadId),

    /**
     * Query function
     * - Delegates data normalization to the API layer.
     */
    queryFn: () =>
      getPriceUploadHistoryDetailsCount(partnerId, uploadId, { onlyFailed }),
  };

  return useQuery(queryOptions);
};
